import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Paginator } from "primereact/paginator";
import { useTranslation } from "react-i18next";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import TableHeader from "../tableHelpers/TableHeader";
import ColumnMenu from "../tableHelpers/ColumnMenu";
import ViewsMenu from "../tableHelpers/ViewMenu";
import {
  getCellColor,
  headerCellStyle,
  cellStyle as tdStyle,
} from "../tableHelpers/TableStyleUtils";
import { AdvanceFilter } from "../../M_Components/M_Table/advanceFilter/AdvanceFilter";
import { useTableFilters } from "../tableHelpers/useTableFilters";
import "./CarrierByLaneTable.css";

// ============================================================================================================
// Helper: baseline best metric per lane from server-side lane summaries
// IMPORTANT:
// - This MUST be computed server-side (/laneSummaries/accepted) and passed down as laneBestMap.
// - FE should not recompute global "best per lane" scanning all quotes again.
// ============================================================================================================
const bestFromLaneBestMap = (laneBestMap, laneId, metricKey) => {
  if (!(laneBestMap instanceof Map) || laneId == null) return null;

  const key = Number(laneId);
  if (!Number.isFinite(key)) return null;

  const b = laneBestMap.get(key);
  if (!b) return null;

  if (metricKey === "bid") return b.bestBidValue ?? null;
  if (metricKey === "loads") return b.bestLoadsValue ?? null;
  if (metricKey === "transit") return b.bestEtdValue ?? null;

  return null;
};

// ============================================================================================================
// Helper: resolves the Tender currency label
// IMPORTANT:
// - In TenderPhase mode, the currency still belongs to the parent Tender.
// - Therefore this component reads currency from tenderInfo.
// ============================================================================================================
const getTenderCurrencyLabel = (tenderInfo) => {
  const rawCurrency =
    tenderInfo?.Currency ??
    tenderInfo?.currency ??
    tenderInfo?.CurrencySymbol ??
    tenderInfo?.currencySymbol ??
    tenderInfo?.CurrencyCode ??
    tenderInfo?.currencyCode ??
    tenderInfo?.CurrencyName ??
    tenderInfo?.currencyName ??
    "";

  if (rawCurrency && typeof rawCurrency === "object") {
    return String(
      rawCurrency.Symbol ??
      rawCurrency.symbol ??
      rawCurrency.Code ??
      rawCurrency.code ??
      rawCurrency.Name ??
      rawCurrency.name ??
      ""
    ).trim();
  }

  return String(rawCurrency ?? "").trim();
};

// ============================================================================================================
// Helper: formats numeric values with the current locale.
// ============================================================================================================
const formatNumber = (value, locale) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));

// ============================================================================================================
// CarrierByLaneTable
// - Renders a matrix Lane x Carrier with a single metric per cell (Bid, Loads or TransitDays).
// - Colors each cell by comparing the carrier value on that lane against the lane best value.
// - Metric to display is controlled by metricKey prop ("bid" | "loads" | "transit").
// - Supports column visibility, views and advanced filters on lane label.
// - Exports the current view to Excel with a top row describing the active metric.
// ============================================================================================================
export default function CarrierByLaneTable({
  tenderInfo,
  quotes,
  lanes,
  carriers,
  onRefresh,
  metricKey = "bid",
  // Baseline best metrics per lane (computed at page level from /laneSummaries/accepted)
  laneBestMap,
}) {
  const { t, i18n } = useTranslation();

  // Base paging state
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  // Anchor elements for menus
  const [anchorAF, setAnchorAF] = useState(null);
  const [anchorEls, setAnchorEls] = useState(null);
  const [anchorV, setAnchorV] = useState(null);

  // Saved views and filters (migrated to shared hook)
  const {
    metaPair,
    views,
    addMetaPair,
    deleteFilter,
    deleteAllFilters,
    submitFilters,
    resetToDefault,
    applyView,
    saveView,
  } = useTableFilters({
    // When applying filters, reset pagination so the user sees results immediately.
    onSubmit: () => setFirst(0),
  });

  // ============================================================================================================
  // Currency formatting
  // - Only BidValue should display the Tender currency.
  // - Loads and TransitDays remain plain numeric values.
  // ============================================================================================================
  const currencyLabel = useMemo(
    () => getTenderCurrencyLabel(tenderInfo),
    [tenderInfo]
  );

  const currentLocale = i18n?.resolvedLanguage || i18n?.language || undefined;

  const formatMetricValue = (value) => {
    if (value == null) return "-";

    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return String(value);
    }

    const formattedValue = formatNumber(numericValue, currentLocale);

    if (metricKey !== "bid" || !currencyLabel) {
      return formattedValue;
    }

    return `${formattedValue} ${currencyLabel}`;
  };

  // ----------------------------------------------------------------------------------------------------------
  // Lane helpers (defensive)
  // - New data uses Inactive; old UI used IsActive
  // - Default behavior: show lane unless explicitly inactive
  // ----------------------------------------------------------------------------------------------------------
  const getLaneId = (l) => l?.LaneId ?? l?.laneId ?? l?.Id ?? l?.id ?? null;

  const getLaneOrigin = (l) =>
    l?.LaneSource ??
    l?.laneSource ??
    l?.Origin ??
    l?.origin ??
    l?.SourceLocation ??
    l?.sourceLocation ??
    "";

  const getLaneDestination = (l) =>
    l?.LaneDestination ??
    l?.laneDestination ??
    l?.Destination ??
    l?.destination ??
    l?.DestinationLocation ??
    l?.destinationLocation ??
    "";

  const isLaneActive = (lane) => {
    if (!lane) return false;
    if (lane.IsActive !== undefined) return !!lane.IsActive;
    if (lane.isActive !== undefined) return !!lane.isActive;
    const inactive = lane.Inactive ?? lane.inactive;
    if (inactive !== undefined) return !inactive;
    return true;
  };

  // Active lanes only
  const activeLanes = useMemo(
    () => (lanes || []).filter(isLaneActive),
    [lanes]
  );

  // ============================================================================================================
  // Lane rows
  // - Each row represents one active lane, identified by LaneId.
  // - UX: split Lane into Origin + Destination for consistency with ViewLaneTable.
  // ============================================================================================================
  const laneRows = useMemo(
    () =>
      activeLanes.map((l) => {
        const origin = getLaneOrigin(l);
        const destination = getLaneDestination(l);
        return {
          id: getLaneId(l),
          origin,
          destination,
          // label kept for any legacy view/filter compatibility
          label: `${origin} -> ${destination}`,
        };
      }),
    [activeLanes]
  );

  // ============================================================================================================
  // Carrier columns
  // - Each column represents one carrier, identified by CarrierId and the carrier name.
  // ============================================================================================================
  const carrierCols = useMemo(
    () =>
      (carriers || []).map((c) => ({
        id: c.CarrierId ?? c.Id,
        name: c.CarrierName ?? c.Name,
      })),
    [carriers]
  );

  // ============================================================================================================
  // Lane x Carrier metrics
  // - For each (laneId, carrierId) combination, keep:
  //   bid     -> lowest BidValue observed for that lane/carrier
  //   loads   -> highest Loads observed for that lane/carrier
  //   transit -> lowest EstimatedTransitDays observed for that lane/carrier
  //
  // NOTE:
  // - We still need to scan quotes once to build the matrix, but we no longer compute lane "best" here.
  //   Best per lane is provided by laneBestMap from the server summaries.
  // ============================================================================================================
  const laneCarrierMetrics = useMemo(() => {
    const outer = new Map(); // laneId -> Map(carrierId -> { bid, loads, transit })

    const activeLaneIds = new Set(activeLanes.map((l) => getLaneId(l)));

    (quotes || [])
      .filter(
        (q) =>
          !(q?.Inactive ?? q?.inactive) &&
          activeLaneIds.has(q?.LaneId ?? q?.laneId)
      )
      .forEach((q) => {
        const laneId = q?.LaneId ?? q?.laneId;
        const carrierId =
          q?.CarrierId ??
          q?.carrierId ??
          q?.Carrier?.Id ??
          q?.carrier?.Id;
        if (!laneId || !carrierId) return;

        let laneMap = outer.get(laneId);
        if (!laneMap) {
          laneMap = new Map();
          outer.set(laneId, laneMap);
        }

        const current = laneMap.get(carrierId) || {
          bid: null,
          loads: null,
          transit: null,
        };

        const bidVal = Number(q?.BidValue ?? q?.bidValue);
        const loadsVal = Number(q?.Loads ?? q?.loads);
        const transitVal = Number(
          q?.EstimatedTransitDays ?? q?.estimatedTransitDays
        );

        if (
          Number.isFinite(bidVal) &&
          (current.bid == null || bidVal < current.bid)
        ) {
          current.bid = bidVal;
        }

        if (
          Number.isFinite(loadsVal) &&
          (current.loads == null || loadsVal > current.loads)
        ) {
          current.loads = loadsVal;
        }

        if (
          Number.isFinite(transitVal) &&
          (current.transit == null || transitVal < current.transit)
        ) {
          current.transit = transitVal;
        }

        laneMap.set(carrierId, current);
      });

    return outer;
  }, [activeLanes, quotes]);

  // ============================================================================================================
  // Metadata for advanced filter component
  // - Updated: filter by Origin and Destination (instead of a single Lane label)
  // ============================================================================================================
  const metaType = useMemo(
    () => [
      {
        metaName: "origin",
        metaDisplay: "Origin",
        metaType: "string",
        metaIsList: false,
      },
      {
        metaName: "destination",
        metaDisplay: "Destination",
        metaType: "string",
        metaIsList: false,
      },
    ],
    []
  );

  // ============================================================================================================
  // Filtered rows based on lane filters
  // ============================================================================================================
  const filteredLaneRows = useMemo(() => {
    let result = [...laneRows];

    metaPair.forEach((filter) => {
      const val = filter.value?.toLowerCase();
      if (!val) return;

      if (filter.name === "origin") {
        if (filter.operation === "Contains") {
          result = result.filter((r) =>
            (r.origin || "").toLowerCase().includes(val)
          );
        }
        if (filter.operation === "Equals") {
          result = result.filter((r) => (r.origin || "").toLowerCase() === val);
        }
      }

      if (filter.name === "destination") {
        if (filter.operation === "Contains") {
          result = result.filter((r) =>
            (r.destination || "").toLowerCase().includes(val)
          );
        }
        if (filter.operation === "Equals") {
          result = result.filter(
            (r) => (r.destination || "").toLowerCase() === val
          );
        }
      }
    });

    return result;
  }, [laneRows, metaPair]);

  // ============================================================================================================
  // Paged rows for display
  // ============================================================================================================
  const pagedLaneRows = useMemo(
    () => filteredLaneRows.slice(first, first + rows),
    [filteredLaneRows, first, rows]
  );

  // ============================================================================================================
  // Excel export
  // - Exports the current filtered view.
  // - First row contains a single cell describing the active metric.
  // - Second row contains headers: Origin | Destination | <Carrier1> | <Carrier2> | ...
  // ============================================================================================================
  const exportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("CarrierByLane");

    const metricLabel =
      metricKey === "bid"
        ? t("BidValue")
        : metricKey === "loads"
          ? t("Loads")
          : t("EstimatedTransitDays");

    const headerRow = [
      t("Origin"),
      t("Destination"),
      ...carrierCols.map((c) => c.name),
    ];

    // Top row with metric description
    const titleRow = [metricLabel, ...Array(headerRow.length - 1).fill("")];
    sheet.addRow(titleRow);

    // Header row
    sheet.addRow(headerRow);

    // Data rows
    filteredLaneRows.forEach((lane) => {
      const laneMap = laneCarrierMetrics.get(lane.id) || new Map();
      const rowValues = carrierCols.map((c) => {
        const metrics = laneMap.get(c.id);
        if (!metrics) return "-";

        const value =
          metricKey === "bid"
            ? metrics.bid
            : metricKey === "loads"
              ? metrics.loads
              : metrics.transit;

        return value != null ? formatMetricValue(value) : "-";
      });

      sheet.addRow([lane.origin, lane.destination, ...rowValues]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "CarrierByLane.xlsx");
  };

  // ============================================================================================================
  // Column menu config
  // - Single columns: "Origin" + "Destination" plus one column per carrier.
  // - User can toggle each carrier column independently.
  // ============================================================================================================
  const singleColumns = useMemo(
    () => [t("Origin"), t("Destination"), ...carrierCols.map((c) => c.name)],
    [carrierCols, t]
  );

  const columnGroups = useMemo(() => [], []);
  const [selectedColumns, setSelectedColumns] = useState(() => [
    t("Origin"),
    t("Destination"),
    ...carrierCols.map((c) => c.name),
  ]);

  // ============================================================================================================
  // Sync selectedColumns when carrier list changes
  // - Keeps only valid columns and adds any new carriers as visible by default.
  // ============================================================================================================
  useEffect(() => {
    setSelectedColumns((prev) => {
      const desired = new Set([
        t("Origin"),
        t("Destination"),
        ...carrierCols.map((c) => c.name),
      ]);
      const kept = prev.filter((c) => desired.has(c));
      const missing = [...desired].filter((c) => !kept.includes(c));
      return [...kept, ...missing];
    });
  }, [carrierCols, t]);

  return (
    // Wrapper without horizontal scroll: header, filters and paginator stay fixed
    <div className="carrier-by-lane-wrapper">
      {/* Main table header with actions */}
      <TableHeader
        filterCount={metaPair.length}
        onRefresh={onRefresh}
        onExport={exportExcel}
        onOpenViews={(e) => setAnchorV(e.currentTarget)}
        onOpenFilters={(e) => setAnchorAF(e.currentTarget)}
        onOpenColumns={(e) => setAnchorEls(e.currentTarget)}
      />

      {/* Column visibility menu */}
      <ColumnMenu
        anchorEl={anchorEls}
        open={Boolean(anchorEls)}
        onClose={() => setAnchorEls(null)}
        singleColumns={singleColumns}
        columnGroups={columnGroups}
        selectedColumns={selectedColumns}
        onChangeSelected={setSelectedColumns}
      />

      {/* Saved views menu */}
      <ViewsMenu
        anchorEl={anchorV}
        open={Boolean(anchorV)}
        onClose={() => setAnchorV(null)}
        viewsMap={views}
        onSelectDefault={() => {
          resetToDefault();
          setAnchorV(null);
        }}
        onSelectView={(view) => {
          applyView(view);
          setAnchorV(null);
        }}
        onCreateNewViewClick={() => {
          setAnchorV(null);
          setAnchorAF(document.getElementById("advFilter"));
        }}
      />

      {/* Advanced filters panel */}
      <AdvanceFilter
        metaPair={metaPair}
        metaType={metaType}
        onAddMetaPair={addMetaPair}
        onSubmitFilters={submitFilters}
        onDeleteFilter={deleteFilter}
        onDeleteAllFilters={deleteAllFilters}
        anchorAF={anchorAF}
        setAnchorAF={setAnchorAF}
        onSaveFilter={(viewName) => {
          // NOTE: Save view via hook so we do not mutate the Map instance in-place.
          saveView(viewName);
        }}
        views={views}
        onToasts={() => { }}
        color="var(--color-red)"
        removeFilters={["Add"]}
      />

      {/* Main table (only this block scrolls horizontally) */}
      <div className="carrier-by-lane-table-container">
        <table className="carrier-by-lane-table">
          <thead>
            <tr>
              {selectedColumns.includes(t("Origin")) && (
                <th
                  style={headerCellStyle()}
                  className="carrier-by-lane-header-cell carrier-by-lane-header-cell-lane"
                >
                  {t("Origin")}
                </th>
              )}

              {selectedColumns.includes(t("Destination")) && (
                <th
                  style={headerCellStyle()}
                  className="carrier-by-lane-header-cell carrier-by-lane-header-cell-lane"
                >
                  {t("Destination")}
                </th>
              )}

              {carrierCols.map((c) =>
                selectedColumns.includes(c.name) ? (
                  <th
                    key={`h-${c.id}`}
                    style={headerCellStyle()}
                    className="carrier-by-lane-header-cell carrier-by-lane-header-cell-numeric"
                  >
                    {c.name}
                  </th>
                ) : null
              )}
            </tr>
          </thead>
          <tbody>
            {pagedLaneRows.map((lane) => {
              const best = bestFromLaneBestMap(laneBestMap, lane.id, metricKey);
              const laneMap = laneCarrierMetrics.get(lane.id) || new Map();

              return (
                <tr key={lane.id}>
                  {selectedColumns.includes(t("Origin")) && (
                    <td
                      style={tdStyle({
                        background: "var(--color-grey)",
                        border: "2px solid var(--color-black)",
                        fontWeight: "bold",
                      })}
                      className="carrier-by-lane-cell carrier-by-lane-cell-lane"
                    >
                      {lane.origin}
                    </td>
                  )}

                  {selectedColumns.includes(t("Destination")) && (
                    <td
                      style={tdStyle({
                        background: "var(--color-grey)",
                        border: "2px solid var(--color-black)",
                        fontWeight: "bold",
                      })}
                      className="carrier-by-lane-cell carrier-by-lane-cell-lane"
                    >
                      {lane.destination}
                    </td>
                  )}

                  {carrierCols.map((c) => {
                    if (!selectedColumns.includes(c.name)) return null;

                    const metrics = laneMap.get(c.id);
                    let value = null;

                    if (metrics) {
                      value =
                        metricKey === "bid"
                          ? metrics.bid
                          : metricKey === "loads"
                            ? metrics.loads
                            : metrics.transit;
                    }

                    const bg =
                      best == null || value == null
                        ? undefined
                        : metricKey === "loads"
                          ? getCellColor(best, value)
                          : getCellColor(value, best);

                    return (
                      <td
                        key={`${lane.id}-${c.id}`}
                        style={tdStyle({
                          background: bg,
                        })}
                        className="carrier-by-lane-cell carrier-by-lane-cell-numeric"
                      >
                        {value != null ? formatMetricValue(value) : "-"}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Paginator
        first={first}
        rows={rows}
        totalRecords={filteredLaneRows.length}
        rowsPerPageOptions={[10, 20, 50]}
        onPageChange={(e) => {
          setFirst(e.first);
          setRows(e.rows);
        }}
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      />
    </div>
  );
}

CarrierByLaneTable.propTypes = {
  tenderInfo: PropTypes.object,
  quotes: PropTypes.array.isRequired,
  lanes: PropTypes.array.isRequired,
  carriers: PropTypes.array.isRequired,
  onRefresh: PropTypes.func.isRequired,
  metricKey: PropTypes.oneOf(["bid", "loads", "transit"]),
  laneBestMap: PropTypes.instanceOf(Map),
};